/**
 * Values the browser remembers (FE Arch 1.0 Step 2, #1166).
 *
 * Every `localStorage`/`sessionStorage` value of the app is declared with
 * {@link createPreference} or {@link createPreferenceFamily}, which own what each
 * module used to copy:
 *
 * - **Key scheme** `bf.<name>.v<version>[.<entity>][.u<userId>]`. Bump `version`
 *   when the stored shape changes incompatibly.
 * - **User scope**, resolved at every read/write (modules load before login):
 *   `'none'`; `'required'` (no user → nothing is read or written); `'optional'`
 *   (no user → the unscoped key).
 * - **Never throws.** Absent, unreadable, corrupt or `parse`-rejected values read
 *   as the default; writes are best effort.
 * - **Legacy keys** from before the scheme are migrated on the first read (old
 *   value written under the new key, old key removed), or with `keepLegacy` only
 *   read through (a shared unscoped value other users may still need).
 * - **Live value** `value$`/`get`/`set` (`usePreference` for components), read from storage on first use,
 *   and, with `crossTab`, kept in step across the browser's tabs on the
 *   `bf-viewer-prefs` channel (`{ type: name, userId, value }`) once `startSync()`
 *   has opened it — at bootstrap, never at module load (Node's channel keeps
 *   Mocha alive).
 *
 * The handles are module-level singletons by design (they ARE the app's storage
 * schema); `resetAllPreferencesForTests()` returns them to a clean state.
 */

import { BehaviorSubject } from 'rxjs';
import { getCurrentUser } from '@brainfocus/web/services/user';
import { BroadcastPort, openBroadcastPort } from '@brainfocus/web/shared/platform/broadcast';

export type UserScope = 'none' | 'required' | 'optional';

export interface LegacyKeyContext {
    userId: number | null;
    entity: string | null;
}

export interface PreferenceOptions<T> {
    version: number;
    /** A fresh default per call, so callers may mutate what they get. */
    defaultValue: () => T;
    /** Validate the parsed JSON; `undefined` means "not a valid value" (the default is used). */
    parse: (raw: unknown) => T | undefined;
    userScope?: UserScope;
    storage?: 'local' | 'session';
    /** Keys this value lived under before the scheme, most specific first. */
    legacyKeys?: (ctx: LegacyKeyContext) => string[];
    /** Read legacy keys through without migrating or removing them. */
    keepLegacy?: boolean;
    /** Keep the live value in step across tabs of the same user. */
    crossTab?: boolean;
}

/** Test/override inputs: an explicit storage (`null` = unavailable) and user (`null` = none). */
export interface StoreOverride {
    storage?: Storage | null;
    userId?: number | null;
}

export interface Preference<T> {
    /** The storage key now, or `null` when nothing may be stored (no user for a required scope). */
    key(o?: StoreOverride): string | null;
    load(o?: StoreOverride): T;
    /** Persist; returns whether the value reached storage. */
    save(value: T, o?: StoreOverride): boolean;
    clear(o?: StoreOverride): void;
    /** Live value for this JS realm (detached viewer windows included). */
    readonly value$: BehaviorSubject<T>;
    get(): T;
    /** Persist, update the live value, and tell the other tabs when `crossTab`. */
    set(value: T): void;
    /** Open the cross-tab channel (`crossTab` only). Idempotent; call at bootstrap. */
    startSync(): void;
}

const PREFS_CHANNEL = 'bf-viewer-prefs';

interface Registered {
    reset(): void;
}

const registry = new Set<Registered>();

function resolveStorage(kind: 'local' | 'session', override: Storage | null | undefined): Storage | null {
    if (override !== undefined) return override;
    try {
        if (kind === 'session') return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
        return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch {
        return null;
    }
}

function resolveUserId(override: number | null | undefined): number | null {
    if (override !== undefined) return override;
    try {
        return getCurrentUser()?.id ?? null;
    } catch {
        return null;
    }
}

interface CrossTabMessage {
    type: string;
    userId: number | null;
    value: unknown;
}

function isCrossTabMessage(data: unknown): data is CrossTabMessage {
    if (typeof data !== 'object' || data === null) return false;
    const m = data as Partial<CrossTabMessage>;
    return typeof m.type === 'string' && 'value' in m && (m.userId === null || typeof m.userId === 'number');
}

function makePreference<T>(name: string, entity: string | null, opts: PreferenceOptions<T>): Preference<T> & Registered {
    const scope = opts.userScope ?? 'none';
    const kind = opts.storage ?? 'local';
    const base = `bf.${name}.v${opts.version}${entity !== null ? `.${entity}` : ''}`;

    const keyFor = (userId: number | null): string | null => {
        if (scope === 'none') return base;
        if (userId === null) return scope === 'required' ? null : base;
        return `${base}.u${userId}`;
    };

    const parseRaw = (raw: string | null): T | undefined => {
        if (raw === null) return undefined;
        try {
            return opts.parse(JSON.parse(raw));
        } catch {
            return undefined;
        }
    };

    const key = (o: StoreOverride = {}): string | null => keyFor(resolveUserId(o.userId));

    const load = (o: StoreOverride = {}): T => {
        const userId = resolveUserId(o.userId);
        const k = keyFor(userId);
        const store = resolveStorage(kind, o.storage);
        if (k === null || !store) return opts.defaultValue();
        try {
            const current = store.getItem(k);
            const legacy = opts.legacyKeys?.({ userId, entity }) ?? [];
            if (current !== null) {
                if (!opts.keepLegacy) legacy.forEach(l => store.removeItem(l));
                return parseRaw(current) ?? opts.defaultValue();
            }
            for (const l of legacy) {
                const raw = store.getItem(l);
                if (raw === null) continue;
                const value = parseRaw(raw);
                if (opts.keepLegacy) {
                    if (value !== undefined) return value;
                    continue;
                }
                store.removeItem(l);
                if (value !== undefined) {
                    store.setItem(k, raw);
                    return value;
                }
            }
        } catch {
            // Storage refused access: preferences are best effort.
        }
        return opts.defaultValue();
    };

    const save = (value: T, o: StoreOverride = {}): boolean => {
        const k = key(o);
        const store = resolveStorage(kind, o.storage);
        if (k === null || !store) return false;
        try {
            store.setItem(k, JSON.stringify(value));
            return true;
        } catch {
            // Quota exceeded or storage unavailable — preferences are best effort.
            return false;
        }
    };

    const clear = (o: StoreOverride = {}): void => {
        const k = key(o);
        const store = resolveStorage(kind, o.storage);
        if (k === null || !store) return;
        try {
            store.removeItem(k);
        } catch {
            // Nothing to clean up.
        }
    };

    let subject: BehaviorSubject<T> | null = null;
    let port: BroadcastPort | null | undefined;

    const startSync = (): void => {
        if (!opts.crossTab || port !== undefined) return;
        port = openBroadcastPort(PREFS_CHANNEL);
        if (!port) return;
        port.onMessage = (data: unknown) => {
            if (!isCrossTabMessage(data) || data.type !== name) return;
            // Another account signed in on the same browser has its own value.
            if (data.userId !== resolveUserId(undefined)) return;
            const value = opts.parse(data.value);
            // The sending tab already persisted it; only the live value changes.
            if (value !== undefined) subjectOf().next(value);
        };
    };

    const subjectOf = (): BehaviorSubject<T> => {
        subject ??= new BehaviorSubject<T>(load());
        return subject;
    };

    const set = (value: T): void => {
        save(value);
        subjectOf().next(value);
        // A channel never delivers to its own sender, so there is no echo.
        port?.postMessage({ type: name, userId: resolveUserId(undefined), value } satisfies CrossTabMessage);
    };

    return {
        key,
        load,
        save,
        clear,
        get value$() { return subjectOf(); },
        get: () => subjectOf().value,
        set,
        startSync,
        reset: () => {
            port?.close();
            port = undefined;
            const store = resolveStorage(kind, undefined);
            if (!store) return;
            try {
                const owned: string[] = [];
                for (let i = 0; i < store.length; i++) {
                    const k = store.key(i);
                    if (k !== null && (k === base || k.startsWith(`${base}.`))) owned.push(k);
                }
                owned.forEach(k => store.removeItem(k));
            } catch {
                // Storage unavailable: nothing was stored either.
            }
            // Same subject, back to the default: whoever holds `value$` keeps listening.
            subject?.next(opts.defaultValue());
        },
    };
}

/** A value the browser remembers under `bf.<name>.v<version>[.u<userId>]`. */
export function createPreference<T>(name: string, opts: PreferenceOptions<T>): Preference<T> {
    const pref = makePreference(name, null, opts);
    registry.add(pref);
    return pref;
}

/** One value per entity (analysis, recording, montage…) under `bf.<name>.v<version>.<entity>[.u<userId>]`. */
export function createPreferenceFamily<T>(name: string, opts: PreferenceOptions<T>): (entity: string | number) => Preference<T> {
    const members = new Map<string, Preference<T> & Registered>();
    registry.add({
        reset: () => {
            members.forEach(m => m.reset());
            members.clear();
            // Members never touched in this realm still own keys under the family prefix.
            makePreference(name, null, opts).reset();
        },
    });
    return (entity) => {
        const id = String(entity);
        let member = members.get(id);
        if (!member) {
            member = makePreference(name, id, opts);
            members.set(id, member);
        }
        return member;
    };
}

/**
 * Remove every stored key of every preference, put every live value back to its
 * default and close every cross-tab port (a later `startSync()` reopens it). Test seam only — run before each test by `test/global_setup.ts`,
 * because jsdom's storage is shared by the whole run.
 */
export function resetAllPreferencesForTests(): void {
    registry.forEach(r => r.reset());
}
